export const TROPHIES = [
  // ── First steps ──
  {
    id: "first_win",
    name: "First Blood",
    desc: "Win your very first game",
    icon: "♔",
    condition: s => s.wins >= 1,
  },
  {
    id: "first_checkmate",
    name: "Domination",
    desc: "Win by checkmate (not resignation)",
    icon: "♚",
    condition: s => s.checkmateWins >= 1,
  },
  {
    id: "speed_demon",
    name: "Swift Strike",
    desc: "Win a game in 15 moves or fewer",
    icon: "✦",
    condition: s => s.fastWins >= 1,
  },
  // ── Bot levels ──
  {
    id: "beat_easy",
    name: "Warm Up",
    desc: "Defeat the Easy bot",
    icon: "♙",
    condition: s => (s.botWins?.easy || 0) >= 1,
  },
  {
    id: "beat_novice",
    name: "Apprentice",
    desc: "Defeat the Novice bot",
    icon: "♗",
    condition: s => (s.botWins?.novice || 0) >= 1,
  },
  {
    id: "beat_intermediate",
    name: "Challenger",
    desc: "Defeat the Intermediate bot",
    icon: "♘",
    condition: s => (s.botWins?.intermediate || 0) >= 1,
  },
  {
    id: "beat_advanced",
    name: "Tactician",
    desc: "Defeat the Advanced bot",
    icon: "♖",
    condition: s => (s.botWins?.advanced || 0) >= 1,
  },
  {
    id: "beat_pro",
    name: "Grandmaster",
    desc: "Defeat the Pro bot",
    icon: "♕",
    condition: s => (s.botWins?.pro || 0) >= 1,
  },
  {
    id: "bot_slayer",
    name: "Slayer of Engines",
    desc: "Beat every bot difficulty level",
    icon: "⚜",
    condition: s =>
      (s.botWins?.easy || 0) >= 1 &&
      (s.botWins?.novice || 0) >= 1 &&
      (s.botWins?.intermediate || 0) >= 1 &&
      (s.botWins?.advanced || 0) >= 1 &&
      (s.botWins?.pro || 0) >= 1,
  },
  // ── Streaks ──
  {
    id: "streak_3",
    name: "Hat Trick",
    desc: "Win 3 games in a row",
    icon: "†",
    condition: s => s.maxStreak >= 3,
  },
  {
    id: "streak_5",
    name: "Relentless",
    desc: "Win 5 games in a row",
    icon: "‡",
    condition: s => s.maxStreak >= 5,
  },
  {
    id: "streak_10",
    name: "Unstoppable",
    desc: "Win 10 games in a row",
    icon: "✶",
    condition: s => s.maxStreak >= 10,
  },
  {
    id: "streak_25",
    name: "Conqueror",
    desc: "Win 25 games in a row",
    icon: "✵",
    condition: s => s.maxStreak >= 25,
  },
  {
    id: "streak_50",
    name: "Sovereign",
    desc: "Win 50 games in a row",
    icon: "⚜",
    condition: s => s.maxStreak >= 50,
  },
  // ── Volume ──
  {
    id: "games_10",
    name: "Initiate",
    desc: "Play 10 games",
    icon: "◆",
    condition: s => s.gamesPlayed >= 10,
  },
  {
    id: "games_50",
    name: "Veteran",
    desc: "Play 50 games",
    icon: "◈",
    condition: s => s.gamesPlayed >= 50,
  },
  {
    id: "games_100",
    name: "Centurion",
    desc: "Play 100 games",
    icon: "✵",
    condition: s => s.gamesPlayed >= 100,
  },
  // ── PvP ──
  {
    id: "pvp_win",
    name: "Head to Head",
    desc: "Win a local PvP game",
    icon: "♞",
    condition: s => s.pvpWins >= 1,
  },
  {
    id: "pvp_5",
    name: "Court Champion",
    desc: "Win 5 PvP games",
    icon: "♛",
    condition: s => s.pvpWins >= 5,
  },
];

export const DEFAULT_STATS = {
  wins: 0,
  losses: 0,
  draws: 0,
  gamesPlayed: 0,
  currentStreak: 0,
  maxStreak: 0,
  checkmateWins: 0,
  fastWins: 0,
  pvpWins: 0,
  botWins:   { easy: 0, novice: 0, intermediate: 0, advanced: 0, pro: 0 },
  botLosses: { easy: 0, novice: 0, intermediate: 0, advanced: 0, pro: 0 },
  earnedTrophies: [], // [{ id, earnedAt }]
};

export function loadStats() {
  try {
    const raw = localStorage.getItem("checkmate_stats");
    if (!raw) return { ...DEFAULT_STATS };
    return { ...DEFAULT_STATS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_STATS };
  }
}

export function saveStats(stats) {
  try { localStorage.setItem("checkmate_stats", JSON.stringify(stats)); } catch {}
}

// Call after every game. Returns array of newly earned trophy IDs.
export function recordResult(stats, result, opts = {}) {
  // result: "win" | "loss" | "draw"
  // opts: { byCheckmate, fastWin, mode, difficulty }
  const next = { ...stats };

  next.gamesPlayed = (next.gamesPlayed || 0) + 1;

  if (result === "win") {
    next.wins = (next.wins || 0) + 1;
    next.currentStreak = (next.currentStreak || 0) + 1;
    next.maxStreak = Math.max(next.maxStreak || 0, next.currentStreak);
    if (opts.byCheckmate) next.checkmateWins = (next.checkmateWins || 0) + 1;
    if (opts.fastWin) next.fastWins = (next.fastWins || 0) + 1;
    if (opts.mode === "pvp") next.pvpWins = (next.pvpWins || 0) + 1;
    if (opts.mode === "bot" && opts.difficulty) {
      next.botWins = { ...next.botWins };
      next.botWins[opts.difficulty] = (next.botWins[opts.difficulty] || 0) + 1;
    }
  } else if (result === "loss") {
    next.losses = (next.losses || 0) + 1;
    next.currentStreak = 0;
    if (opts.mode === "bot" && opts.difficulty) {
      next.botLosses = { ...next.botLosses };
      next.botLosses[opts.difficulty] = (next.botLosses[opts.difficulty] || 0) + 1;
    }
  } else {
    next.draws = (next.draws || 0) + 1;
    next.currentStreak = 0;
  }

  // Check for newly earned trophies
  const alreadyEarned = new Set((next.earnedTrophies || []).map(t => t.id));
  const newlyEarned = [];
  for (const trophy of TROPHIES) {
    if (!alreadyEarned.has(trophy.id) && trophy.condition(next)) {
      newlyEarned.push(trophy.id);
      next.earnedTrophies = [...(next.earnedTrophies || []), { id: trophy.id, earnedAt: Date.now() }];
    }
  }

  saveStats(next);
  return { updatedStats: next, newlyEarned };
}
